/**
 * My Reminders Page Component
 * Reuses the schedule row layout (rialto-schedule-item) with a delete icon in place of the bell.
 * Has Upcoming (default) and History tabs.
 */

import { useState, useEffect, useCallback } from '@wordpress/element';
import { useAuth } from '../context/AuthContext';
import apiFetch from '@wordpress/api-fetch';
import RemindersLanding from './RemindersLanding';

export default function MyReminders() {
    const { isAuthenticated, getAuthHeader, logout, clearAuth } = useAuth();
    const [activeTab, setActiveTab] = useState('upcoming');
    const [upcomingAlerts, setUpcomingAlerts] = useState([]);
    const [historyAlerts, setHistoryAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [trailerModal, setTrailerModal] = useState(null);

    const siteUrl = window.rialtoData?.siteUrl || '/';

    const fetchAlerts = useCallback(async (filter = 'upcoming') => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        if (filter === 'history') {
            setHistoryLoading(true);
        } else {
            setLoading(true);
        }

        try {
            const res = await apiFetch({
                path: `rialto/v1/alerts?filter=${filter}`,
                headers: getAuthHeader(),
            });
            if (filter === 'history') {
                setHistoryAlerts(res.alerts || []);
                setHistoryLoaded(true);
            } else {
                setUpcomingAlerts(res.alerts || []);
            }
        } catch (err) {
            if (err?.code === 'rest_forbidden' || err?.code === 'not_authenticated' || err?.data?.status === 401) {
                clearAuth();
            } else {
                console.error('Failed to load reminders:', err);
            }
        } finally {
            if (filter === 'history') {
                setHistoryLoading(false);
            } else {
                setLoading(false);
            }
        }
    }, [isAuthenticated, getAuthHeader, clearAuth]);

    // Fetch upcoming on mount / auth change
    useEffect(() => {
        fetchAlerts('upcoming');
    }, [isAuthenticated]);

    // Fetch history when tab is first switched to
    useEffect(() => {
        if (activeTab === 'history' && !historyLoaded && isAuthenticated) {
            fetchAlerts('history');
        }
    }, [activeTab, historyLoaded, isAuthenticated]);

    // Update bell badge/dot in header (based on upcoming count)
    useEffect(() => {
        const el = document.getElementById('rialto-bell-dot');
        if (!el) return;
        if (upcomingAlerts.length > 0) {
            el.className = 'header-bell-badge';
            el.textContent = upcomingAlerts.length;
        } else {
            el.className = 'header-bell-dot';
            el.textContent = '';
        }
    }, [upcomingAlerts]);

    // Handle ESC key for trailer modal
    useEffect(() => {
        if (!trailerModal) return;
        const onKey = (e) => { if (e.key === 'Escape') closeTrailerModal(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [trailerModal]);

    const handleDelete = async (e, alertId) => {
        e.stopPropagation();
        if (!confirm('Remove this reminder?')) return;

        setDeleting(alertId);
        try {
            await apiFetch({
                path: `rialto/v1/alerts/${alertId}`,
                method: 'DELETE',
                headers: getAuthHeader(),
            });
            setUpcomingAlerts((prev) => prev.filter((a) => a.id !== alertId));
            setHistoryAlerts((prev) => prev.filter((a) => a.id !== alertId));
        } catch (err) {
            console.error('Failed to delete reminder:', err);
        } finally {
            setDeleting(null);
        }
    };

    const handleTrailer = (e, film) => {
        e.stopPropagation();
        if (film.trailer_embed) {
            setTrailerModal({
                title: film.title,
                embedUrl: film.trailer_embed + '?autoplay=1&rel=0',
            });
        } else if (film.youtube_trailer || film.trailer_url) {
            const url = film.youtube_trailer || film.trailer_url;
            const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
            if (match) {
                setTrailerModal({
                    title: film.title,
                    embedUrl: `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`,
                });
            }
        }
    };

    const closeTrailerModal = () => {
        setTrailerModal(null);
    };

    const handleRowClick = (url, e) => {
        if (e.target.closest('.action-btn') || e.target.closest('a')) return;
        if (url && url !== '#') {
            window.location.href = url;
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const formatTime12hr = (dateStr) => {
        const d = new Date(dateStr);
        let h = d.getHours();
        const m = d.getMinutes().toString().padStart(2, '0');
        const period = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return { time: `${h}:${m}`, period };
    };

    const formatDateLabel = (dateStr) => {
        const d = new Date(dateStr);
        const now = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

        if (isSameDay(d, now)) return 'Today';
        if (isSameDay(d, tomorrow)) return 'Tomorrow';

        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
    };

    const formatReminderLabel = (mins) => {
        if (mins >= 60) {
            const hrs = mins / 60;
            return hrs === 1 ? '1hr before' : `${hrs}hrs before`;
        }
        return `${mins}m before`;
    };

    const renderAlertRow = (alert, isHistory = false) => {
        const film = alert.film || {};
        const posterUrl = film.poster_thumb || film.poster;
        const filmUrl = film.url || '#';
        const hasTrailer = !!(film.trailer_url || film.trailer_embed || film.youtube_trailer);
        const timeObj = formatTime12hr(alert.screening_datetime);
        const dateLabel = formatDateLabel(alert.screening_datetime);
        const mobileTime = `${timeObj.time} ${timeObj.period} \u2022 ${dateLabel}`;

        return (
            <div
                key={alert.id}
                className={`rialto-schedule-item mr-item${isHistory ? ' mr-history-item' : ''}`}
                onClick={(e) => handleRowClick(filmUrl, e)}
                role="button"
                tabIndex={0}
            >
                {/* Time Column - desktop only */}
                <div className="schedule-time-col" style={{ justifyContent: 'center', gap: '2px', alignSelf: 'center' }}>
                    <span className="schedule-time">
                        <span className="time-digits">{timeObj.time}</span>
                        <span className="time-period">{timeObj.period}</span>
                    </span>
                    <span className="mr-date-label">{dateLabel}</span>
                </div>

                {/* Poster */}
                <div className="schedule-poster">
                    {posterUrl ? (
                        <img src={posterUrl} alt={film.title} loading="lazy" />
                    ) : (
                        <div className="poster-placeholder">
                            <span>&#127916;</span>
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="schedule-content" data-time={mobileTime}>
                    <h3 className="schedule-title">{film.title}</h3>
                    <div className="schedule-meta">
                        {film.year && <span className="meta-year">{film.year}</span>}
                        {film.genres?.length > 0 && (
                            <span className="meta-genre">{film.genres[0]}</span>
                        )}
                        {film.duration && (
                            <span className="meta-duration">{film.duration} min</span>
                        )}
                        {film.certification && (
                            <span className={`meta-cert cert-${film.certification.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
                                {film.certification}
                            </span>
                        )}
                    </div>
                    <p className="schedule-synopsis mr-reminder-info">
                        {isHistory ? (
                            <>
                                {alert.status === 'sent' && 'Sent'}
                                {alert.status === 'failed' && 'Failed to send'}
                                {alert.status === 'pending' && 'Expired'}
                                {' \u2022 '}
                                {formatReminderLabel(alert.reminder_minutes)}
                            </>
                        ) : (
                            <>
                                Reminder: {formatReminderLabel(alert.reminder_minutes)}
                                {alert.status === 'failed' && ' \u2022 Retrying'}
                            </>
                        )}
                    </p>
                </div>

                {/* Actions */}
                <div className="schedule-ratings-actions">
                    <div className="schedule-actions">
                        {!isHistory && (
                            <button
                                className="action-btn remind-btn mr-delete-btn"
                                onClick={(e) => handleDelete(e, alert.id)}
                                disabled={deleting === alert.id}
                                title="Remove reminder"
                            >
                                {deleting === alert.id ? (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" opacity="0.3"/>
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"/>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    </svg>
                                )}
                            </button>
                        )}
                        {hasTrailer && (
                            <button
                                className="action-btn trailer-btn"
                                onClick={(e) => handleTrailer(e, film)}
                                title="Watch Trailer"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Arrow */}
                <div className="schedule-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"/>
                    </svg>
                </div>
            </div>
        );
    };

    const alerts = activeTab === 'upcoming' ? upcomingAlerts : historyAlerts;
    const isTabLoading = activeTab === 'upcoming' ? loading : historyLoading;

    // Not authenticated - show landing page
    if (!isAuthenticated && !loading) {
        return (
            <RemindersLanding
                onAuthSuccess={() => { setLoading(true); fetchAlerts('upcoming'); }}
            />
        );
    }

    return (
        <div className="my-reminders">
            <div className="my-reminders__header">
                <h1 className="my-reminders__title">My Reminders</h1>
                <button className="my-reminders__logout" onClick={logout}>Sign Out</button>
            </div>

            {/* Tabs */}
            <div className="mr-tabs">
                <button
                    className={`mr-tab${activeTab === 'upcoming' ? ' mr-tab--active' : ''}`}
                    onClick={() => handleTabChange('upcoming')}
                >
                    Upcoming
                    {upcomingAlerts.length > 0 && (
                        <span className="mr-tab__count">{upcomingAlerts.length}</span>
                    )}
                </button>
                <button
                    className={`mr-tab${activeTab === 'history' ? ' mr-tab--active' : ''}`}
                    onClick={() => handleTabChange('history')}
                >
                    History
                    {historyAlerts.length > 0 && (
                        <span className="mr-tab__count mr-tab__count--grey">{historyAlerts.length}</span>
                    )}
                </button>
            </div>

            {isTabLoading ? (
                <div className="my-reminders__loading">Loading...</div>
            ) : alerts.length === 0 ? (
                <div className="my-reminders__empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity: 0.4, marginBottom: 16}}>
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                    {activeTab === 'upcoming' ? (
                        <>
                            <p>No upcoming reminders.</p>
                            <p style={{fontSize: '0.9rem', opacity: 0.6}}>
                                Browse the <a href={siteUrl + 'schedule/'} style={{color: 'var(--rialto-gold)'}}>schedule</a> and tap the bell icon to set reminders.
                            </p>
                        </>
                    ) : (
                        <p>No reminder history yet.</p>
                    )}
                </div>
            ) : (
                <div className="rialto-schedule-list">
                    {alerts.map((alert) => renderAlertRow(alert, activeTab === 'history'))}
                </div>
            )}

            {/* Trailer Modal */}
            {trailerModal && (
                <div className="trailer-modal-overlay" onClick={closeTrailerModal}>
                    <div className="trailer-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="trailer-modal-header">
                            <h3>{trailerModal.title}</h3>
                            <button className="trailer-modal-close" onClick={closeTrailerModal}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <div className="trailer-modal-content">
                            <iframe
                                src={trailerModal.embedUrl}
                                title={trailerModal.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
